import { useState } from 'react'
import {
	Glyphs,
	MiniTabbedWidgetWrapper,
	WidgetInputComplexNumericField,
	WidgetLabel,
	WidgetSelectGroup,
	type WidgetSelectGroupItem,
} from '../../../../rapid-ui'
import type { ContentXPin, ContentYPin, Layer } from '../../data/v1'
import { BoxTabIcon, LayoutTabIcon, TextFormattingTabIcon } from '../../glyphs/tabs'
import { useDesignerActions } from '../../use-designer-actions'
import { BoxInspector } from './box'
import { TextFormattingInspector } from './text-formatting'

const xPinItems: WidgetSelectGroupItem<ContentXPin>[] = [
	{ id: 'left', label: 'Left', render: () => <Glyphs.core.AlignXLeftIcon /> },
	{ id: 'center', label: 'Center', render: () => <Glyphs.core.AlignXCenterIcon /> },
	{ id: 'right', label: 'Right', render: () => <Glyphs.core.AlignXRightIcon /> },
	{ id: 'stretch', label: 'Stretch', render: () => <Glyphs.core.AlignXStretchIcon /> },
]

const yPinItems: WidgetSelectGroupItem<ContentYPin>[] = [
	{ id: 'top', label: 'Top', render: () => <Glyphs.core.AlignYTopIcon /> },
	{ id: 'center', label: 'Center', render: () => <Glyphs.core.AlignYCenterIcon /> },
	{ id: 'bottom', label: 'Bottom', render: () => <Glyphs.core.AlignYBottomIcon /> },
]

export type ContentLayerInspectorProps = {
	layer: Extract<Layer, { type: 'content' }>
}

export function ContentLayerInspector({ layer }: ContentLayerInspectorProps) {
	const { updateLayer } = useDesignerActions()
	const [tab, setTab] = useState<'layout' | 'formatting' | 'box'>('layout')

	return (
		<MiniTabbedWidgetWrapper
			value={tab}
			onChange={setTab}
			tabs={[
				{
					id: 'layout',
					icon: <LayoutTabIcon />,
					label: 'Layout',
					children: (
						<div className="flex flex-col gap-2">
							<WidgetLabel label="X pin">
								<WidgetSelectGroup
									value={layer.xPin}
									onChange={(value) => updateLayer(layer.id, { xPin: value })}
									items={xPinItems}
								/>
							</WidgetLabel>

							<WidgetLabel label="Y pin">
								<WidgetSelectGroup
									value={layer.yPin}
									onChange={(value) => updateLayer(layer.id, { yPin: value })}
									items={yPinItems}
								/>
							</WidgetLabel>

							<WidgetLabel label="Gap">
								<WidgetInputComplexNumericField
									value={layer.gap}
									onChange={(gap) => updateLayer(layer.id, { gap })}
									min={0}
									suffix="px"
								/>
							</WidgetLabel>
						</div>
					),
				},
				{
					id: 'formatting',
					icon: <TextFormattingTabIcon />,
					label: 'Formatting',
					children: (
						<TextFormattingInspector
							textAlignMode={layer.textAlignMode}
							textAlignValue={layer.textAlignValue}
							onTextAlignModeChange={(m) => updateLayer(layer.id, { textAlignMode: m })}
							onTextAlignValueChange={(v) => updateLayer(layer.id, { textAlignValue: v })}
							textStyle={layer.textStyle}
							onTextStyleChange={(changes) =>
								updateLayer(layer.id, (prev) => ({
									textStyle: { ...(prev as typeof layer).textStyle, ...changes },
								}))}
						/>
					),
				},
				{
					id: 'box',
					icon: <BoxTabIcon />,
					label: 'Box',
					children: (
						<BoxInspector
							layerId={layer.id}
							spacing={layer.spacing}
							onSpacingChange={(spacing) => updateLayer(layer.id, { spacing })}
							onTransientSpacingChange={(spacing) =>
								updateLayer(layer.id, { spacing }, { transient: true })}
						/>
					),
				},
			]}
		/>
	)
}
